import { IssueWithJourney } from '@tom-ui/operations'
import { IssueResponseDto, IssueType as PlanningIssueType } from '@tom-ui/planning/app/api'
import { IssueCard } from '@tom-ui/planning/pages/Issues/Components/molecules/IssueCard'
import { useViewportHeightCalc } from '@tom-ui/ui'
import { YardIssueCard, YardIssueTypeDummy } from '@tom-ui/yard'
import { observer } from 'mobx-react-lite'
import { IssueType as OperationIssueType } from 'modules/operations/src/app/api'
import { FC, useMemo } from 'react'
import { VariableSizeList as List } from 'react-window'
import { AggregateIssuesViewStore } from '../../Stores/AggregateIssuesViewStore'
import { OperationsIssueCard } from '../OperationsIssueCard'

type Props = { aggregateIssuesViewStore: AggregateIssuesViewStore }
type IssueItemProps = { index: number; style: React.CSSProperties } & Props

export const IssuesList: FC<Props> = observer(({ aggregateIssuesViewStore }) => {
  const listHeight = useViewportHeightCalc({ difference: 250 })
  const isOpreationIssue = useMemo(
    () =>
      Object.values(OperationIssueType).includes(
        aggregateIssuesViewStore.selectedIssueType as OperationIssueType,
      ),
    [aggregateIssuesViewStore.selectedIssueType],
  )

  const isYardIssue = useMemo(
    () =>
      Object.values(YardIssueTypeDummy).includes(
        aggregateIssuesViewStore.selectedIssueType as YardIssueTypeDummy,
      ),
    [aggregateIssuesViewStore.selectedIssueType],
  )

  const operationIssues = aggregateIssuesViewStore.operationDataFilteredByIssueStatus

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: listHeight,
      }}
    >
      {isYardIssue ? (
        <List
          height={listHeight}
          itemCount={aggregateIssuesViewStore.yardDataFilteredByIssueStatus.length}
          itemSize={() => 120}
          width='100%'
        >
          {({ index, style }) => YardIssue({ aggregateIssuesViewStore, index, style })}
        </List>
      ) : isOpreationIssue ? (
        <List
          height={listHeight}
          itemCount={operationIssues.length}
          itemSize={index => (operationIssues[index].containerJourneyData ? 250 : 85)}
          width='100%'
        >
          {({ index, style }) => OperationIssue({ aggregateIssuesViewStore, index, style })}
        </List>
      ) : (
        <List
          height={listHeight}
          itemCount={aggregateIssuesViewStore.planningDataFilteredByIssueStatus.length}
          itemSize={index => {
            const issue = aggregateIssuesViewStore.planningDataFilteredByIssueStatus[index] as any
            const issueData: IssueResponseDto = issue?.data

            // Different issue types cards have different heights
            const heightMap: Record<PlanningIssueType, number> = {
              [PlanningIssueType.CustomsInspectionRequired]: 260,
              [PlanningIssueType.CustomsPending]: 290,
              [PlanningIssueType.CustomsRefused]: 260,
              [PlanningIssueType.MissingConsignee]: 265,
              [PlanningIssueType.MissingOperator]: 150,
              [PlanningIssueType.MissingRailcar]: 260,
              [PlanningIssueType.MissingUnNumberOfUnitLabel]: 270,
              [PlanningIssueType.Shortlanded]: 260,
              [PlanningIssueType.UnknownCompany]: 265,
              [PlanningIssueType.MissingCompany]: 265,
              [PlanningIssueType.Unknown]: 260,
              [PlanningIssueType.Overlanded]: 260,
            }

            if (issueData && heightMap[issueData.issueType]) {
              return heightMap[issueData.issueType]
            }

            return 150
          }}
          width='100%'
        >
          {({ index, style }) => PlanningIssue({ aggregateIssuesViewStore, index, style })}
        </List>
      )}
    </div>
  )
})

const PlanningIssue = ({ aggregateIssuesViewStore, index, style }: IssueItemProps) => {
  const issue = aggregateIssuesViewStore.planningDataFilteredByIssueStatus[index] as any

  return (
    <div style={{ ...style }}>
      <IssueCard
        // it could not find any prop extended from IEntity<T> in IssueItem. For an interim solution, we have to cast to any here first
        key={`planning_issue_${(issue as any).id}`}
        issueItem={issue}
        onClickResolve={() => {
          aggregateIssuesViewStore.planningStores.issueResolutionViewStore.openDialog(issue)
        }}
      />
    </div>
  )
}

const YardIssue = ({ aggregateIssuesViewStore, index, style }: IssueItemProps) => {
  const issue = aggregateIssuesViewStore.yardDataFilteredByIssueStatus[index]

  return (
    <div style={{ ...style }}>
      <YardIssueCard
        key={`yard_issue_${issue.issueId}`}
        issue={issue}
        hasResolve
        onClickResolve={() => aggregateIssuesViewStore.toggleYardIssueResolve(issue)}
      />
    </div>
  )
}

const OperationIssue = ({ aggregateIssuesViewStore, index, style }: IssueItemProps) => {
  const { containerJourneyItemStore } = aggregateIssuesViewStore.planningStores.issueViewStore

  const getContainerJourney = (issue: IssueWithJourney) => {
    if (!issue.orderId) return

    return (
      containerJourneyItemStore.journeysByInboundOrderId[issue.orderId] ??
      containerJourneyItemStore.journeysByOutboundOrderId[issue.orderId]
    )
  }

  const issue = aggregateIssuesViewStore.operationDataFilteredByIssueStatus[index]

  return (
    <div style={{ ...style }}>
      <OperationsIssueCard
        key={`operations_issue_${issue.id}`}
        store={aggregateIssuesViewStore}
        issue={issue}
        containerJourney={getContainerJourney(issue)}
        hasResolve
      />
    </div>
  )
}
